/**
 * ATL-1487 — a dual-write the backend REFUSED: Firestore committed, Supabase did not, and nothing
 * the device can do will change that.
 *
 * WHAT A REFUSAL IS. The backend answers a write it will not apply with 409 or 422 — a unique
 * constraint other than the record's own identity (`foreign_unique_conflict`: createSupply since
 * ATL-782, createManufacture since ATL-1445 stage 2a, and every other books create), a money leg
 * that is buried or belongs to another document (`money_leg_*`, ATL-1445/1501), a replay whose row
 * vanished or whose state is unreadable (`replay_*`, ATL-1481), an id truncated at `#`, a refund over
 * the sale's total (422). Every one of those messages says the same thing: NOT saved.
 *
 * It is never a benign duplicate. Since #425/#427 every idempotent path answers a re-send of a write
 * that already landed with SUCCESS (`resolveReplayedCreate` returns the stored row), so a conflict
 * that still reaches the device is, by that contract, a write Supabase does not hold while Firestore
 * does. A 409 that meant "already there, nothing to do" would be a backend defect — the fix for it
 * is a 2xx, not a quieter client.
 *
 * WHY IT NEEDED ITS OWN CLASS. `isPermanentReplayFailure` has always known 409/422 are permanent,
 * so they are not queued — correctly: retrying cannot change the answer. But the live path's
 * loudness was decided by `classifyDualWriteDefect`, which only knows 400, so a refused write was
 * reported at `warning`, fingerprinted into the shared per-op transient bucket beside a 503 — and
 * not queued. A loss with no retry and no alert: the one combination nothing downstream recovers.
 *
 * WHY POSTHOG AS WELL AS SENTRY. Sentry is on a 5,000/month plan that has refused every event for
 * weeks at a time (ATL-1354). A permanent loss must be countable per organization and per release
 * even then, so each refusal is also a `dual_write_refused` operational event.
 *
 * WHY THE CODE, NOT THE MESSAGE. The backend puts a machine-readable `code` on the refusal body
 * (`ConflictException({ code, message })`); the fingerprint and the event are keyed on it, so one
 * kind of refusal is one issue and renaming a message cannot split or merge them. A refusal without
 * a code is still loud — `uncoded` — because being refused is what makes it a loss, not having a
 * code; the code only says which kind.
 */
import { captureOperationalEvent } from '../analytics/operationalTelemetry'

/**
 * The HTTP status, across both client error shapes: `ApiRequestError` (typed client) carries
 * `statusCode`; a raw axios rejection (`apiClient.js`, and every outbox replay) carries
 * `response.status`. The one extraction both the live path and the replay classifiers read.
 */
export function failureStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined
  const e = error as Record<string, unknown>
  if (typeof e.statusCode === 'number') return e.statusCode
  const response = e.response
  if (response && typeof response === 'object') {
    const status = (response as Record<string, unknown>).status
    if (typeof status === 'number') return status
  }
  return undefined
}

/**
 * 409 Conflict and 422 Unprocessable Entity: the request was well-formed and was evaluated, and the
 * STATE refused it. 400 is not here — a malformed payload is `classifyDualWriteDefect`'s class, with
 * its own field-level fingerprint.
 */
const REFUSED_STATUSES: ReadonlySet<number> = new Set([409, 422])

export function isRefusedWriteFailure(error: unknown): boolean {
  const status = failureStatus(error)
  return status !== undefined && REFUSED_STATUSES.has(status)
}

/** The code recorded for a refusal whose body carries none (or one we will not put in a tag). */
export const UNCODED_REFUSAL = 'uncoded'

/**
 * Backend codes are identifiers (`foreign_unique_conflict`, `money_leg_buried`,
 * `TRUNCATED_ID_AMBIGUOUS`). Anything else is not used as a fingerprint or tag value: a code built
 * from data would turn one issue into one per record.
 */
const CODE_SHAPE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/

const codeOf = (body: unknown): string | undefined => {
  if (!body || typeof body !== 'object') return undefined
  const code = (body as Record<string, unknown>).code
  return typeof code === 'string' && CODE_SHAPE.test(code) ? code : undefined
}

/**
 * The refusal's machine-readable `code`, or {@link UNCODED_REFUSAL}.
 *
 * Three places it can be, one per error shape:
 *  - `ApiRequestError.error.code` — `normalizeApiErrorBody` lifts it out of the envelope;
 *  - raw axios, `response.data.message.code` — `AllExceptionsFilter` sends
 *    `{ statusCode, timestamp, path, message: <the exception's body> }`, so an exception thrown as
 *    `{ code, message }` arrives one level in;
 *  - raw axios, `response.data.code` — a flat body, for anything answering without the envelope.
 */
export function refusalCode(error: unknown): string {
  if (!error || typeof error !== 'object') return UNCODED_REFUSAL
  const e = error as Record<string, unknown>
  const data = (e.response as Record<string, unknown> | undefined)?.data
  const envelopeInner =
    data && typeof data === 'object' ? (data as Record<string, unknown>).message : undefined
  return codeOf(e.error) ?? codeOf(envelopeInner) ?? codeOf(data) ?? UNCODED_REFUSAL
}

/**
 * Count one refused write in PostHog. `path` separates a live write refused on its first attempt
 * from a queued one refused on replay (a transient failure first, then a refusal) — both are the
 * same loss, and the population is only complete with both.
 *
 * Counts and kinds only, per `operationalTelemetry`'s rule: no document id, no org id (PostHog
 * resolves the organization from the person). The id stays on the console line and the Sentry
 * context, where an incident responder has it.
 */
export function reportRefusedWrite(
  label: string,
  error: unknown,
  path: 'live' | 'replay',
): void {
  const i = label.indexOf('.')
  captureOperationalEvent('dual_write_refused', {
    dual_write: label,
    dual_write_module: i === -1 ? label : label.slice(0, i),
    dual_write_op: i === -1 ? '' : label.slice(i + 1),
    status: failureStatus(error) ?? null,
    code: refusalCode(error),
    path,
  })
}
